// Sales → one account: details, agreement, onboarding, invoices, status, audit.
// Each card hides its writes from a role that cannot make them; the server
// refuses them anyway (app_platform_can).

import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { fill, type Locale } from "../../i18n/dictionary";
import type { SalesDictionary } from "../../i18n/salesDictionary";
import { auditChanges, ONBOARDING_STEPS, type AuditEntry, type SalesAccount } from "../../data/sales";
import type { Invoice, OrgLifecycle } from "../../data/types";
import {
  Button,
  Card,
  Chip,
  ConfirmDialog,
  ErrorPanel,
  Field,
  INPUT,
  LoadingLine,
  Notice,
  TableCard,
  TD,
  TH,
  formatDate,
  formatMoney,
  formatNumber,
  looksLikeEmail,
  todayIso,
  useLoad,
  type Tone,
} from "../admin/ui";
import { ReadOnlyNote, StatusLine, invoiceChip, keeps, salesErrorCopy, shareProblem, statusChip, useSales } from "./shared";

const DAY = 86_400_000;
type Msg = { tone: Tone; text: string } | null;

export function AccountDetail() {
  const { orgId = "" } = useParams();
  const { ds, t, locale } = useSales();
  const load = useLoad(() => ds.accounts(), [ds]);
  const [version, setVersion] = useState(0);
  const changed = () => {
    setVersion((v) => v + 1);
    void load.refresh();
  };

  const back = (
    <Link to="/sales" style={{ color: "var(--brand)", fontWeight: 600, display: "inline-flex", minHeight: 44, alignItems: "center" }}>
      <span aria-hidden="true" className="zchev" style={{ display: "inline-block" }}>←</span>&nbsp;{t.accounts.back}
    </Link>
  );

  if (load.loading && !load.data) return <LoadingLine label={t.common.loading} />;
  if (load.error || !load.data) {
    return <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />;
  }
  const a = load.data.find((x) => x.orgId === orgId);
  if (!a) {
    return (
      <div>
        {back}
        <Notice tone="attention">{t.common.errNotFound}</Notice>
      </div>
    );
  }
  const st = statusChip(t, a.status);

  return (
    <article aria-labelledby="acc-name">
      {back}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: "6px 0 4px" }}>
        <h2 id="acc-name" style={{ fontSize: 24, color: "var(--ink-deep)", margin: 0, overflowWrap: "anywhere" }}>
          {a.name}
        </h2>
        <Chip tone={st.tone}>{st.label}</Chip>
      </div>
      <p style={{ margin: "0 0 16px", color: "var(--ink-soft)", fontSize: 14.5 }}>
        {a.kind === "individual" ? t.common.kindIndividual : t.common.kindOrg} · {fill(t.accounts.since, { date: formatDate(locale, a.createdAt) })}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 14 }}>
        <DetailsCard key={`d-${a.orgId}`} a={a} onChanged={changed} />
        <AgreementCard key={`a-${a.orgId}`} a={a} onChanged={changed} />
        <OnboardingCard orgId={a.orgId} version={version} />
        <StatusCard a={a} onChanged={changed} />
        <InvoicesCard a={a} onChanged={changed} />
        <AuditCard orgId={a.orgId} version={version} />
      </div>
    </article>
  );
}

// ---- Details -------------------------------------------------------------------------

function Grid({ children }: { children: ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 10 }}>{children}</div>;
}

function TextArea({ id, label, value, onChange, disabled, maxLength }: { id: string; label: string; value: string; onChange: (v: string) => void; disabled?: boolean; maxLength: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: "1 / -1" }}>
      <label htmlFor={id} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
        {label}
      </label>
      <textarea id={id} value={value} maxLength={maxLength} rows={3} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ ...INPUT, resize: "vertical" }} />
    </div>
  );
}

const DETAIL_FIELDS = [
  ["legal_name", "legalName", 200],
  ["registration_no", "registrationNo", 60],
  ["country", "country", 2],
  ["tax_id", "taxId", 60],
  ["contact_name", "contactName", 120],
  ["contact_email", "contactEmail", 254],
  ["contact_phone", "contactPhone", 40],
  ["sales_owner", "salesOwner", 120],
  ["source", "source", 120],
] as const;

function DetailsCard({ a, onChanged }: { a: SalesAccount; onChanged: () => void }) {
  const { ds, t, can } = useSales();
  const editable = can("accounts");
  const [v, setV] = useState<Record<string, string>>(() => ({
    legal_name: a.legalName ?? "",
    registration_no: a.registrationNo ?? "",
    country: a.country,
    tax_id: a.taxId ?? "",
    contact_name: a.contactName ?? "",
    contact_email: a.contactEmail ?? "",
    contact_phone: a.contactPhone ?? "",
    sales_owner: a.salesOwner ?? "",
    source: a.source ?? "",
    billing_address: a.billingAddress ?? "",
    notes: a.notes ?? "",
  }));
  const [billingEmail, setBillingEmail] = useState(a.billingEmail ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const set = (k: string) => (val: string) => setV((s) => ({ ...s, [k]: val }));

  async function save() {
    if ((v.contact_email.trim() && !looksLikeEmail(v.contact_email)) || (billingEmail.trim() && !looksLikeEmail(billingEmail))) {
      setMsg({ tone: "attention", text: t.common.errInvalid });
      return;
    }
    if (v.country.trim() && !/^[A-Za-z]{2}$/.test(v.country.trim())) {
      setMsg({ tone: "attention", text: t.common.errInvalid });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const fields: Record<string, string | null> = {};
      for (const [k, val] of Object.entries(v)) fields[k] = val.trim() === "" ? null : val.trim();
      await ds.saveAccount(a.orgId, fields);
      // The billing email lives on the licence (console_set_licence keeps any
      // field sent as null).
      if (billingEmail.trim() && billingEmail.trim() !== (a.billingEmail ?? "")) {
        await ds.setLicence(a.orgId, {
          seats: null,
          familyAllowance: null,
          familyPrice: null,
          familyRetailPrice: null,
          sstRate: null,
          currency: null,
          billingEmail: billingEmail.trim(),
          contractEnd: null,
        });
      }
      setMsg({ tone: "good", text: t.common.saved });
      onChanged();
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t.details.title}>
      <Grid>
        {DETAIL_FIELDS.map(([key, label, max]) => (
          <Field
            key={key}
            label={t.details[label]}
            value={v[key]}
            maxLength={max}
            disabled={!editable}
            type={key === "contact_email" ? "email" : key === "contact_phone" ? "tel" : "text"}
            onChange={(e) => set(key)(e.target.value)}
          />
        ))}
        <Field label={t.details.billingEmail} type="email" value={billingEmail} maxLength={254} disabled={!editable || !can("agreements")} onChange={(e) => setBillingEmail(e.target.value)} />
        <TextArea id={`addr-${a.orgId}`} label={t.details.billingAddress} value={v.billing_address} maxLength={500} disabled={!editable} onChange={set("billing_address")} />
        <TextArea id={`notes-${a.orgId}`} label={t.details.notes} value={v.notes} maxLength={4000} disabled={!editable} onChange={set("notes")} />
      </Grid>
      <p style={{ margin: "12px 0 0", fontSize: 13.5, color: "var(--ink-soft)", overflowWrap: "anywhere" }}>
        <strong>{t.details.admins}:</strong> {a.adminEmails.join(", ") || t.common.none}
      </p>
      {editable ? (
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? t.common.saving : t.common.save}
          </Button>
          <StatusLine msg={msg} />
        </div>
      ) : (
        <ReadOnlyNote />
      )}
    </Card>
  );
}

// ---- Agreement -------------------------------------------------------------------------

function AgreementCard({ a, onChanged }: { a: SalesAccount; onChanged: () => void }) {
  const { ds, t, can, locale } = useSales();
  const editable = can("agreements") && can("accounts");
  const [retail, setRetail] = useState(String(a.familyRetailPriceMonthly));
  const [share, setShare] = useState(String(a.familyPriceMonthly));
  const [sst, setSst] = useState(String(a.sstRate));
  const [currency, setCurrency] = useState(a.currency);
  const [licences, setLicences] = useState(String(a.familyAllowance));
  const initialTerms = a.paymentTermsDays === 14 || a.paymentTermsDays === 30 ? String(a.paymentTermsDays) : "other";
  const [terms, setTerms] = useState(initialTerms);
  const [days, setDays] = useState(String(a.paymentTermsDays));
  const [contractEnd, setContractEnd] = useState(a.contractEnd ?? "");
  const [notes, setNotes] = useState(a.agreementNotes ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const cur = currency.trim().toUpperCase() || a.currency;

  async function save() {
    const problem = shareProblem(t, retail, share, sst);
    if (problem) return setMsg({ tone: "attention", text: problem });
    const termDays = terms === "other" ? Number(days) : Number(terms);
    if (!Number.isInteger(termDays) || termDays < 0 || termDays > 120) return setMsg({ tone: "attention", text: t.agreement.errTerms });
    const lic = Number(licences);
    if (!Number.isInteger(lic) || lic < 0 || !/^[A-Za-z]{3}$/.test(currency.trim())) return setMsg({ tone: "attention", text: t.common.errInvalid });
    setBusy(true);
    setMsg(null);
    try {
      await ds.setLicence(a.orgId, {
        seats: null,
        familyAllowance: lic,
        familyPrice: share.trim() === "" ? 0 : Number(share),
        familyRetailPrice: retail.trim() === "" ? 0 : Number(retail),
        sstRate: sst.trim() === "" ? 0 : Number(sst),
        currency: currency.trim().toUpperCase(),
        billingEmail: null,
        contractEnd: contractEnd || null,
      });
      await ds.saveAccount(a.orgId, { payment_terms_days: termDays, agreement_notes: notes.trim() || null });
      setMsg({ tone: "good", text: t.common.saved });
      onChanged();
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t.agreement.title}>
      <Grid>
        <Field label={t.agreement.retail} type="number" min={0} step="0.01" inputMode="decimal" value={retail} disabled={!editable} onChange={(e) => setRetail(e.target.value)} />
        <Field label={t.agreement.share} type="number" min={0} step="0.01" inputMode="decimal" value={share} disabled={!editable} onChange={(e) => setShare(e.target.value)} />
        <Field label={t.agreement.keeps} readOnly tabIndex={-1} value={formatMoney(locale, keeps(retail, share), cur)} hint={t.agreement.keepsHint} />
        <Field label={t.agreement.sst} type="number" min={0} max={100} step="0.01" inputMode="decimal" value={sst} disabled={!editable} onChange={(e) => setSst(e.target.value)} />
        <Field label={t.agreement.currency} maxLength={3} value={currency} disabled={!editable} onChange={(e) => setCurrency(e.target.value)} />
        <Field label={t.agreement.licences} type="number" min={0} value={licences} disabled={!editable} onChange={(e) => setLicences(e.target.value)} />
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label htmlFor={`terms-${a.orgId}`} style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-soft)" }}>
            {t.agreement.terms}
          </label>
          <select id={`terms-${a.orgId}`} value={terms} disabled={!editable} onChange={(e) => setTerms(e.target.value)} style={INPUT}>
            <option value="14">{t.agreement.terms14}</option>
            <option value="30">{t.agreement.terms30}</option>
            <option value="other">{t.agreement.termsOther}</option>
          </select>
        </div>
        {terms === "other" && (
          <Field label={t.agreement.termsDays} type="number" min={0} max={120} value={days} disabled={!editable} onChange={(e) => setDays(e.target.value)} />
        )}
        <Field label={t.agreement.contractEnd} type="date" value={contractEnd} disabled={!editable} onChange={(e) => setContractEnd(e.target.value)} />
        <TextArea id={`agr-notes-${a.orgId}`} label={t.agreement.notes} value={notes} maxLength={2000} disabled={!editable} onChange={setNotes} />
      </Grid>
      {editable ? (
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? t.common.saving : t.agreement.save}
          </Button>
          <StatusLine msg={msg} />
        </div>
      ) : (
        <ReadOnlyNote />
      )}
    </Card>
  );
}

// ---- Onboarding ------------------------------------------------------------------------

function OnboardingCard({ orgId, version }: { orgId: string; version: number }) {
  const { ds, t, locale } = useSales();
  const load = useLoad(() => ds.onboarding(orgId), [ds, orgId, version]);
  return (
    <Card title={t.onboarding.title}>
      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : (
        <>
          <p style={{ margin: "0 0 10px", fontSize: 14, color: "var(--ink-soft)" }}>
            {fill(t.onboarding.progress, { done: ONBOARDING_STEPS.filter((s) => load.data![s]).length, total: ONBOARDING_STEPS.length })}
          </p>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {ONBOARDING_STEPS.map((s) => {
              const when = load.data![s];
              return (
                <li key={s} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "none",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      background: when ? "var(--sage-deep)" : "transparent",
                      color: when ? "var(--surface)" : "var(--ink-faint)",
                      border: when ? "none" : "2px solid var(--line-strong)",
                    }}
                  >
                    {when ? "✓" : ""}
                  </span>
                  <span style={{ fontSize: 14.5 }}>
                    <span style={{ fontWeight: 600, color: "var(--ink-deep)" }}>{t.onboarding[s]}</span>
                    <span style={{ display: "block", fontSize: 13, color: when ? "var(--ink-soft)" : "var(--ink-faint)" }}>
                      {when ? fill(t.onboarding.doneOn, { date: formatDate(locale, when) }) : t.common.notYet}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </Card>
  );
}

// ---- Status ----------------------------------------------------------------------------

function StatusCard({ a, onChanged }: { a: SalesAccount; onChanged: () => void }) {
  const { ds, t, can } = useSales();
  const [target, setTarget] = useState<OrgLifecycle | null>(null);
  const [endLicences, setEndLicences] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const st = statusChip(t, a.status);
  const options: { to: OrgLifecycle; label: string }[] =
    a.status === "active"
      ? [
          { to: "suspended", label: t.status.suspend },
          { to: "closed", label: t.status.close },
        ]
      : [{ to: "active", label: t.status.reactivate }, ...(a.status === "suspended" ? [{ to: "closed" as const, label: t.status.close }] : [])];

  async function confirm() {
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      await ds.setOrgStatus(a.orgId, target, target !== "active" && endLicences);
      setTarget(null);
      setEndLicences(false);
      setDone(t.status.saved);
      onChanged();
    } catch (err) {
      setError(salesErrorCopy(t, err));
    } finally {
      setBusy(false);
    }
  }

  const warn = target === "suspended" ? t.status.warnSuspend : target === "closed" ? t.status.warnClose : t.status.warnReactivate;

  return (
    <Card title={t.status.title}>
      <Chip tone={st.tone}>{st.label}</Chip>
      {can("org_status") ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          {options.map((o) => (
            <Button key={o.to} variant="secondary" onClick={() => setTarget(o.to)}>
              {o.label}
            </Button>
          ))}
        </div>
      ) : (
        <ReadOnlyNote />
      )}
      <p role="status" aria-live="polite" style={{ margin: "10px 0 0", fontSize: 14, color: "var(--sage-deep)", fontWeight: 600 }}>
        {done}
      </p>
      {target && (
        <ConfirmDialog
          title={`${options.find((o) => o.to === target)?.label ?? ""}: ${a.name}`}
          body={warn}
          confirmLabel={t.status.confirm}
          cancelLabel={t.common.cancel}
          busy={busy}
          error={error}
          onConfirm={() => void confirm()}
          onCancel={() => {
            setTarget(null);
            setEndLicences(false);
            setError("");
          }}
        >
          {target !== "active" && (
            <div style={{ marginTop: 14 }}>
              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 15, color: "var(--ink)", cursor: "pointer" }}>
                <input type="checkbox" checked={endLicences} onChange={(e) => setEndLicences(e.target.checked)} style={{ width: 22, height: 22, marginTop: 1, flex: "none" }} />
                {t.status.endLicences}
              </label>
              {endLicences && (
                <div style={{ marginTop: 10 }}>
                  <Notice tone="attention">{t.status.endLicencesWarn}</Notice>
                </div>
              )}
            </div>
          )}
        </ConfirmDialog>
      )}
    </Card>
  );
}

// ---- Invoices --------------------------------------------------------------------------

function lastMonth(): { start: string; end: string } {
  const d = new Date();
  const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
  const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) - DAY);
  return { start: todayIso(start.getTime()), end: todayIso(end.getTime()) };
}

function InvoicesCard({ a, onChanged }: { a: SalesAccount; onChanged: () => void }) {
  const { ds, t, can, locale } = useSales();
  const lm = lastMonth();
  const [start, setStart] = useState(lm.start);
  const [end, setEnd] = useState(lm.end);
  const [amount, setAmount] = useState("");
  const [due, setDue] = useState(() => todayIso(Date.now() + a.paymentTermsDays * DAY));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const invoices = useLoad(() => ds.invoices(a.orgId), [ds, a.orgId]);
  const valid = !!start && !!end && end >= start;
  const estimate = useLoad(() => (valid && can("invoices") ? ds.previewInvoice(a.orgId, start, end) : Promise.resolve(null)), [ds, a.orgId, start, end, valid]);

  async function issue() {
    if (!valid) return setMsg({ tone: "attention", text: t.common.errInvalid });
    const amt = amount.trim() === "" ? null : Number(amount);
    if (amt !== null && (!Number.isFinite(amt) || amt < 0)) return setMsg({ tone: "attention", text: t.common.errInvalid });
    setBusy(true);
    setMsg(null);
    try {
      await ds.issueInvoice(a.orgId, { periodStart: start, periodEnd: end, amount: amt, due: due || null, note: note.trim() || null });
      setMsg({ tone: "good", text: t.invoices.issued });
      setAmount("");
      setNote("");
      await invoices.refresh();
      await estimate.refresh();
      onChanged();
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(inv: Invoice, status: Invoice["status"]) {
    setRowBusy(inv.id);
    try {
      await ds.setInvoiceStatus(inv.id, status);
      await invoices.refresh();
      onChanged();
    } catch (err) {
      setMsg({ tone: "attention", text: salesErrorCopy(t, err) });
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <Card title={t.invoices.title} style={{ gridColumn: "1 / -1" }}>
      {can("invoices") && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, color: "var(--ink-deep)", margin: "0 0 10px" }}>{t.invoices.issueTitle}</h3>
          <Grid>
            <Field label={t.invoices.periodStart} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            <Field label={t.invoices.periodEnd} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            <Field label={t.invoices.amount} type="number" min={0} step="0.01" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} hint={t.invoices.amountHint} />
            <Field label={t.invoices.due} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            <TextArea id={`inv-note-${a.orgId}`} label={t.invoices.note} value={note} maxLength={500} onChange={setNote} />
          </Grid>
          {estimate.data && (
            <p style={{ margin: "10px 0 0", fontSize: 14, color: "var(--ink-soft)" }} aria-live="polite">
              {fill(t.invoices.preview, {
                lm: formatNumber(locale, estimate.data.licenceMonths),
                subtotal: formatMoney(locale, estimate.data.subtotal, estimate.data.currency),
                rate: formatNumber(locale, estimate.data.sstRate),
                sst: formatMoney(locale, estimate.data.sstAmount, estimate.data.currency),
                total: formatMoney(locale, estimate.data.amount, estimate.data.currency),
              })}
            </p>
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Button onClick={() => void issue()} disabled={busy}>
              {t.invoices.issue}
            </Button>
            <StatusLine msg={msg} />
          </div>
        </div>
      )}
      {invoices.loading && !invoices.data ? (
        <LoadingLine label={t.common.loading} />
      ) : !invoices.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, invoices.error)} onRetry={invoices.reload} retryLabel={t.common.retry} />
      ) : invoices.data.length === 0 ? (
        <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.invoices.none}</p>
      ) : (
        <InvoiceTable list={invoices.data} t={t} locale={locale} canChange={can("invoices")} rowBusy={rowBusy} onStatus={(inv, s) => void setStatus(inv, s)} />
      )}
    </Card>
  );
}

function InvoiceTable({
  list,
  t,
  locale,
  canChange,
  rowBusy,
  onStatus,
}: {
  list: Invoice[];
  t: SalesDictionary;
  locale: Locale;
  canChange: boolean;
  rowBusy: string | null;
  onStatus: (inv: Invoice, s: Invoice["status"]) => void;
}) {
  return (
    <TableCard label={t.invoices.title} minWidth={canChange ? 940 : 780}>
      <thead>
        <tr>
          <th scope="col" style={TH}>{t.invoices.colNumber}</th>
          <th scope="col" style={TH}>{t.invoices.colPeriod}</th>
          <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.invoices.colLm}</th>
          <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.invoices.colSubtotal}</th>
          <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.invoices.colSst}</th>
          <th scope="col" style={{ ...TH, textAlign: "end" }}>{t.invoices.colTotal}</th>
          <th scope="col" style={TH}>{t.invoices.colDue}</th>
          <th scope="col" style={TH}>{t.invoices.colStatus}</th>
          {canChange && <th scope="col" style={TH}>{t.invoices.colActions}</th>}
        </tr>
      </thead>
      <tbody>
        {list.map((inv) => {
          const chip = invoiceChip(t, inv.status);
          return (
            <tr key={inv.id}>
              <th scope="row" style={{ ...TD, fontWeight: 600, whiteSpace: "nowrap", textAlign: "start" }}>{inv.number}</th>
              <td style={{ ...TD, whiteSpace: "nowrap" }}>
                {formatDate(locale, inv.periodStart)} – {formatDate(locale, inv.periodEnd)}
              </td>
              <td style={{ ...TD, textAlign: "end" }}>{formatNumber(locale, inv.licenceMonths)}</td>
              <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>{formatMoney(locale, inv.subtotal, inv.currency)}</td>
              <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap" }}>
                {formatMoney(locale, inv.sstAmount, inv.currency)}
                <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-faint)" }}>{formatNumber(locale, inv.sstRate)}%</span>
              </td>
              <td style={{ ...TD, textAlign: "end", whiteSpace: "nowrap", fontWeight: 600 }}>{formatMoney(locale, inv.amount, inv.currency)}</td>
              <td style={{ ...TD, whiteSpace: "nowrap" }}>{formatDate(locale, inv.dueAt)}</td>
              <td style={TD}>
                <Chip tone={chip.tone}>{chip.label}</Chip>
              </td>
              {canChange && (
                <td style={TD}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {inv.status !== "paid" && inv.status !== "void" && (
                      <Button variant="secondary" disabled={rowBusy !== null} aria-label={`${t.invoices.markPaid}: ${inv.number}`} onClick={() => onStatus(inv, "paid")}>
                        {t.invoices.markPaid}
                      </Button>
                    )}
                    {inv.status !== "void" && (
                      <Button variant="secondary" disabled={rowBusy !== null} aria-label={`${t.invoices.markVoid}: ${inv.number}`} onClick={() => onStatus(inv, "void")}>
                        {t.invoices.markVoid}
                      </Button>
                    )}
                    {inv.status !== "issued" && (
                      <Button variant="secondary" disabled={rowBusy !== null} aria-label={`${t.invoices.markIssued}: ${inv.number}`} onClick={() => onStatus(inv, "issued")}>
                        {t.invoices.markIssued}
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </TableCard>
  );
}

// ---- Audit -----------------------------------------------------------------------------

/** "contact_email" → "Contact email". */
export function fieldLabel(key: string): string {
  const s = key.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A stored value, as a person would read it. */
export function auditValue(locale: Locale, v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(v)) return formatDate(locale, v);
    return v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map((x) => auditValue(locale, x)).join(", ");
  return JSON.stringify(v);
}

function entityLabel(t: SalesDictionary, e: AuditEntry): string {
  const key = `entity_${e.entity}` as keyof SalesDictionary["audit"];
  return key in t.audit ? t.audit[key] : t.audit.entity_other;
}

const MAX_CHANGES = 8;

function AuditCard({ orgId, version }: { orgId: string; version: number }) {
  const { ds, t, locale } = useSales();
  const load = useLoad(() => ds.auditLog(orgId), [ds, orgId, version]);
  return (
    <Card title={t.audit.title} style={{ gridColumn: "1 / -1" }}>
      {load.loading && !load.data ? (
        <LoadingLine label={t.common.loading} />
      ) : !load.data ? (
        <ErrorPanel title={t.common.errorTitle} message={salesErrorCopy(t, load.error)} onRetry={load.reload} retryLabel={t.common.retry} />
      ) : load.data.length === 0 ? (
        <p style={{ margin: 0, color: "var(--ink-soft)" }}>{t.audit.none}</p>
      ) : (
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {load.data.map((e) => {
            const changes = auditChanges(e);
            const tpl = e.action === "insert" ? t.audit.created : e.action === "delete" ? t.audit.deleted : t.audit.updated;
            return (
              <li key={e.id} style={{ borderBottom: "1px solid var(--line)", paddingBottom: 10 }}>
                <p style={{ margin: 0, fontSize: 14.5, color: "var(--ink-deep)", overflowWrap: "anywhere" }}>
                  <strong>{fill(tpl, { who: e.actorEmail ?? t.audit.system, what: entityLabel(t, e).toLowerCase() })}</strong>
                </p>
                <p style={{ margin: "2px 0 6px", fontSize: 13, color: "var(--ink-faint)" }}>
                  <time dateTime={e.at}>
                    {formatDate(locale, e.at)} {new Date(e.at).toLocaleTimeString(locale === "ms" ? "ms-MY" : "en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </time>
                </p>
                {changes.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-faint)" }}>{t.audit.noFieldChanges}</p>
                ) : (
                  <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13.5, color: "var(--ink-soft)", display: "flex", flexDirection: "column", gap: 2 }}>
                    {changes.slice(0, MAX_CHANGES).map((c) => (
                      <li key={c.field} style={{ overflowWrap: "anywhere" }}>
                        <span style={{ fontWeight: 600 }}>{fieldLabel(c.field)}:</span>{" "}
                        {e.action === "update" ? (
                          <>
                            <del style={{ color: "var(--ink-faint)" }}>{auditValue(locale, c.before)}</del> <span aria-hidden="true" className="zchev" style={{ display: "inline-block" }}>→</span>
{" "}
                            <ins style={{ textDecoration: "none", color: "var(--ink-deep)" }}>{auditValue(locale, c.after)}</ins>
                          </>
                        ) : (
                          auditValue(locale, e.action === "delete" ? c.before : c.after)
                        )}
                      </li>
                    ))}
                    {changes.length > MAX_CHANGES && <li>{fill(t.audit.more, { n: changes.length - MAX_CHANGES })}</li>}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
